/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2022 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 * Copyright 2024 (c) Daniel Littfinski, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import { UserAction } from './UserActions';
import { createReducer } from '@reduxjs/toolkit';
import { UserClient } from 'Api/generated';

export interface IUsersState {
  UsersRequested: boolean;
  Users: {
    // Key is UserClient.Name+UserClient.Id
    [key: string]: UserClient;
  };
  userNames: UserClient['Username'][];
  requestedToDelete: UserClient[];
  deleted: UserClient[];
  created: UserClient[];
}

const initialState: IUsersState = {
  UsersRequested: false,
  Users: {},
  userNames: [],
  requestedToDelete: [],
  deleted: [],
  created: [],
};

const UserReducer = createReducer<IUsersState>(initialState, (builder) => {
  builder.addCase(UserAction.Delete, (state: IUsersState, action) => {
    const requestedToDelete = [...state.requestedToDelete, action.payload];
    return {
      ...state,
      requestedToDelete,
    };
  });

  builder.addCase(UserAction.Deleted, (state: IUsersState, action) => {
    const deleted = [...state.deleted, action.payload];
    const Users = { ...state.Users };
    if (action.payload.Id) {
      delete Users[action.payload.Id];
    }
    const userName = new Set(state.userNames);
    userName.delete(action.payload.Username);
    return {
      ...state,
      userNames: Array.from(userName),
      Users: { ...Users },
      deleted,
      requestedToDelete: state.requestedToDelete.filter((el) => el !== action.payload),
    };
  });

  builder.addCase(UserAction.Query, (state) => {
    return {
      ...state,
      UsersRequested: true,
    };
  });

  const arrayToObject = (arr: any[], key1: string, key2: string) => {
    return Object.assign({}, ...arr.map((item) => ({ [item[key1] + item[key2]]: item })));
  };

  builder.addCase(UserAction.Queried, (state: IUsersState, action) => {
    const userNames = action.payload.map((user) => user.Username);
    return {
      ...state,
      UsersRequested: false,
      Users: arrayToObject(action.payload, 'Username', 'Id'),
      userNames,
    };
  });

  builder.addCase(UserAction.Updated, (state, action) => {
    const Users = { ...state.Users };
    if (action.payload.Username && action.payload.Id) {
      Users[action.payload.Username + action.payload.Id] = action.payload;
      const userNames = [...state.userNames];
      userNames.push(action.payload.Username);
      //ToDo edit UserName without rerender (replace old Username with new one)
    }
    return {
      ...state,
      Users,
    };
  });
});

export default UserReducer;
